import { habitScheduledOn, habitPausedOn, scheduleSubject, shiftDateKey } from './habit-schedule';
import { deadlineDateKey, daysBetweenKeys } from './deadline';
/** Is this task on a plan for the given date? */
function scheduledOn(task, date, schedules) {
    return schedules.some((s) => s.date === date
        && s.scheduled_tasks.some((slot) => !slot.is_buffer && slot.task_id === task.id));
}
/** Is this task on ANY plan at all? */
function scheduledAnywhere(task, schedules) {
    return schedules.some((s) => s.scheduled_tasks.some((slot) => !slot.is_buffer && slot.task_id === task.id));
}
/**
 * Paused on this day? A pause can carry `paused_until`; once that day has
 * passed the work is live again. Every surface asks this rather than reading
 * `is_paused`, which stays true after an expired pause until something
 * rewrites the task — so the scheduler planned the work while Dashboard
 * still filed it under Paused, Analytics left the habit out, and the
 * unscheduled list said "paused".
 */
export function isPausedOn(task, date) {
    return habitPausedOn(task, date);
}
/** Open work: not finished, not archived. The baseline for every scope. */
export function isOpen(task) {
    // Pause is deliberately NOT considered here: a pause can carry a
    // `paused_until` date and therefore expire, so "is this paused" is a
    // question about a DAY, not about the task alone. `tasksInScope` applies
    // it with the date it already has.
    return task.status !== 'completed' && !task.is_archived;
}
/**
 * The local date a task is due, or null — read by `deadlineDateKey`, so a
 * deadline means the same day on every surface whatever format stored it.
 */
export function deadlineKey(task, timezone) {
    return deadlineDateKey(task.deadline, timezone);
}
/**
 * Whole days until the deadline from `today`; negative once it has passed,
 * null without one. Read the same way as `isOverdue`, so a task can never be
 * "due in 0 days" and "overdue" at once.
 */
export function deadlineDaysLeft(task, today, timezone) {
    const due = deadlineKey(task, timezone);
    return due === null ? null : daysBetweenKeys(today, due);
}
/** Due on exactly this date — habit occurrence, deadline, or a planned slot. */
export function isDueOn(task, date, schedules, timezone) {
    if (task.is_habit)
        return habitScheduledOn(scheduleSubject(task), date);
    if (deadlineKey(task, timezone) === date)
        return true;
    if (task.scheduled_for?.slice(0, 10) === date)
        return true;
    return scheduledOn(task, date, schedules);
}
/**
 * Overdue: past its deadline and still open.
 *
 * Habits are excluded, and that is a rule rather than an oversight. A habit is
 * never overdue — it was due on a day that has passed. Listing a missed
 * Tuesday run as "overdue" turns one skipped day into a permanent item of
 * guilt that can never be cleared.
 */
export function isOverdue(task, today, timezone) {
    if (task.is_habit)
        return false;
    const due = deadlineKey(task, timezone);
    return due !== null && due < today;
}
/** The tasks in one scope. Always open work; never mutates its input. */
export function tasksInScope(tasks, scope, { today, schedules = [], weekDays = 7, timezone }) {
    /**
     * Paused work is excluded, using the date rather than the flag alone.
     *
     * A paused task or habit is one the user deliberately set aside, and
     * listing it under "what's on today" undoes that decision — which is the
     * whole point of pausing. But a pause can carry `paused_until`, and one
     * that has passed means the work is live again, so `habitPausedOn` (which
     * knows both rules) decides rather than a bare `is_paused` check.
     */
    const open = tasks.filter((t) => isOpen(t) && !habitPausedOn(scheduleSubject(t), today));
    switch (scope) {
        case 'today':
            return open.filter((t) => isDueOn(t, today, schedules, timezone));
        case 'tomorrow': {
            const date = shiftDateKey(today, 1);
            return open.filter((t) => isDueOn(t, date, schedules, timezone));
        }
        case 'week': {
            const days = Array.from({ length: weekDays }, (_, i) => shiftDateKey(today, i));
            const end = shiftDateKey(today, weekDays);
            return open.filter((t) => {
                if (t.is_habit)
                    return days.some((d) => habitScheduledOn(scheduleSubject(t), d));
                const due = deadlineKey(t, timezone);
                if (due !== null && due >= today && due < end)
                    return true;
                return days.some((d) => scheduledOn(t, d, schedules));
            });
        }
        case 'overdue':
            return open.filter((t) => isOverdue(t, today, timezone));
        case 'inbox':
            return open.filter((t) => t.needs_review);
        case 'unscheduled':
            return open.filter((t) => !t.is_habit && !t.deadline && !t.scheduled_for
                && !scheduledAnywhere(t, schedules));
        case 'all':
        default:
            return open;
    }
}
//# sourceMappingURL=task-scope.js.map